import _ from 'lodash';

import {
  navigateToStaticPageBySlug,
  navigateToDetailPageBySlug,
} from '../helpers/NavigationHelper';
import {selectCmsItemsForLocales} from '@packages/systems/cms/client-selectors';
import {
  getNativeLinkNodeFromEventTarget,
  getNativeId,
  NORMAL_NODE,
} from '@packages/shared/utilities/DomUtils';
import {either, allPass, anyPass} from '@packages/utilities/fp/utils';
import {
  DATA_ATTR_NODE_TYPE,
  NODE_TYPE_COMMERCE_ADD_TO_CART_FORM,
  NODE_TYPE_COMMERCE_CART_WRAPPER,
  DATA_ATTR_OPEN_PRODUCT,
  NODE_TYPE_COMMERCE_DOWNLOADS_BUTTON,
  CHANGE_CART_EVENT,
} from '@packages/systems/commerce/constants';
import {getPaginationPageNumber} from '@packages/systems/dynamo/utils/PaginationUtils';
import {getCollectionItemBySlug} from '@packages/systems/cms/client';
import {PAGINATION_PAGE_SWITCHED} from '@packages/systems/designer/actions';
import {LocaleUtils} from '@packages/systems/localization';
import {CollectionStateType} from '@packages/systems/core/records/CollectionStateType';
import {Locale} from '@packages/systems/localization-types';
import {Component, Expressions} from '@packages/systems/wfdl';
import {getGuidNullable} from '@packages/systems/core/utils/NativeIdUtils';
import {
  getBody,
  expandUserComponents,
  closestPaginationPrevious,
  closestPaginationNext,
} from '@packages/utilities/platform';
import {
  isUserLogOutLogInType,
  isCommerceBuyNowButton,
  isCommerceCheckoutPlaceOrderButton,
  isCommerceCartCheckoutButton,
} from '@packages/utilities/elementTypeUtils';
import {EElement, ExpressionElement} from '@packages/systems/wfdl-ast';
import {closestDynamoWrapperElement} from '@packages/systems/dynamo/utils/DynamoElementUtils';
import {getNodeNativeIdPath} from '@packages/shared/utilities/ElementDomUtils';

const getAttribute = (attr: string) => (target: HTMLElement | SVGElement) =>
  target.getAttribute(attr);
const getTagName = (target: HTMLElement | SVGElement) =>
  (target.tagName || '').toLowerCase();
const getAttributeType = getAttribute('type');
const getAttributeHref = getAttribute('href');
const getAttributeTarget = getAttribute('target');

const isCheckbox = (element: HTMLElement | SVGElement | null): boolean => {
  return Boolean(element && getAttributeType(element) === 'checkbox');
};

const isRadio = (element: HTMLElement | SVGElement | null): boolean => {
  return Boolean(element && getAttributeType(element) === 'radio');
};

const isLabel = (element: HTMLElement | SVGElement) => {
  return Boolean(element && getAttributeType(element) === 'label');
};

const isAnySiblingCheckboxRadio = (
  target: HTMLElement | SVGElement
): boolean => {
  if (!target.parentElement) return false;

  const siblings = Array.from(target.parentElement.children).filter(
    (child) => child !== target
  );

  return siblings.some(
    (sibling) =>
      sibling.nodeType === NORMAL_NODE &&
      (isCheckbox(sibling as HTMLElement | SVGElement) ||
        isRadio(sibling as HTMLElement | SVGElement))
  );
};

const hasNestedCheckboxOrRadio = (
  element: HTMLElement | SVGElement
): boolean => {
  // Check if the element itself is a checkbox or radio
  if (isCheckbox(element) || isRadio(element)) {
    return true;
  }

  // Check all child elements recursively
  for (const child of Array.from(element.children)) {
    if (
      child.nodeType === NORMAL_NODE &&
      hasNestedCheckboxOrRadio(child as HTMLElement | SVGElement)
    ) {
      return true;
    }
  }

  return false;
};

const isLabelWithChildCheckboxRadio = allPass([
  isLabel,
  (target: HTMLElement | SVGElement) => Boolean(target.children?.length),
  hasNestedCheckboxOrRadio,
]);

const isLabelWithChildCheckboxRadioOrIsCheckboxRadio = either(
  isAnySiblingCheckboxRadio,
  either(either(isCheckbox, isRadio), isLabelWithChildCheckboxRadio)
);

const SUPPORTED_INPUT_TYPES = new Set([
  'date',
  'week',
  'time',
  'month',
  'datetime-local',
  'color',
]);

const SUPPORTED_ELEMENT_TYPES = new Set(['details', 'summary']);

const isSupportedElementType = (target: HTMLElement | SVGElement) =>
  SUPPORTED_ELEMENT_TYPES.has(getTagName(target));

// Toggling a popover or showing and closing a modal dialog is the invoker
// button's own native activation behavior, so preview has to let the click
// through untouched — the `event.preventDefault()` fallthrough below would
// otherwise swallow it. A Modal invoker needs both `commandfor` and `command`;
// a button with only one of them has no native behavior to let through.
// Ancestor matching covers clicks that land on content nested inside the
// invoker. Published sites never run this handler, and the canvas withholds
// native popover and modal behavior entirely, so this only unblocks Designer
// preview.
const isNativeInvoker = (target: HTMLElement | SVGElement) =>
  target.closest('[popovertarget], button[commandfor][command]') != null;

const isSupportedFormInput = (target: HTMLElement | SVGElement) => {
  const attrType = getAttributeType(target);

  return attrType && SUPPORTED_INPUT_TYPES.has(attrType);
};

const isSupportedFormInteraction = anyPass([
  isLabelWithChildCheckboxRadioOrIsCheckboxRadio,
  isSupportedFormInput,
]);

const isSupportedInteraction = anyPass([
  isSupportedFormInteraction,
  isSupportedElementType,
  isNativeInvoker,
]);

function maybeGetNativeLottieElementFromTarget(
  target: HTMLElement | SVGElement
) {
  let element: HTMLElement | SVGElement | ParentNode = target;

  while (element != null) {
    // @ts-expect-error - TS2339 - Property 'dataset' does not exist on type 'ParentNode'.
    if (element.dataset && element.dataset.animationType === 'lottie') {
      return element;
    }

    if (element.parentNode == null) {
      return target;
    }

    element = element.parentNode;
  }

  return target;
}

/**
 * This function is called when an element on the Canvas is clicked while in preview mode.
 */
export async function onPreviewClick({
  components,
  event,
  paginationPageNumbers,
  dispatch,
  hrefPrefix,
  onInternalLinkClicked,
  onElementClicked,
}: {
  components: Component.ComponentMap;
  event: React.MouseEvent<HTMLElement, MouseEvent>;
  paginationPageNumbers: {[key: string]: number};
  dispatch: (arg0: {
    type: typeof PAGINATION_PAGE_SWITCHED;
    payload: {
      wrapperNativeIdPath: string[];
      pageNumber: number;
    };
  }) => void;
  hrefPrefix: string | null | undefined;
  onInternalLinkClicked: (href: string) => Promise<void>;
  onElementClicked: (element: ExpressionElement) => void;
}) {
  const clickTarget = getClickTarget(event);

  if (!clickTarget || !clickTarget.nodeName || !clickTarget.getAttribute) {
    event.preventDefault();
    return;
  }

  if (clickTarget.nodeName.toLowerCase() === 'code-island') {
    // Do not handle clicks on code islands, let events pass through so the code components behave as users expect
    return;
  }

  // Catch form submissions
  if (getAttributeType(clickTarget) === 'submit') {
    onPreviewFormSubmit(event, clickTarget);
    event.preventDefault();
    return;
  }

  // Block downloads button from triggering file download in Preview
  if (
    clickTarget.getAttribute(DATA_ATTR_NODE_TYPE) ===
    NODE_TYPE_COMMERCE_DOWNLOADS_BUTTON
  ) {
    event.preventDefault();
    window.alert('Downloads are not available in preview mode.');
    return;
  }

  // Some native form element events are permitted in preview mode
  if (isSupportedInteraction(clickTarget)) {
    return;
  }

  const body = getBody(components);
  if (!body) return;

  const root = Expressions.getElement(expandUserComponents(components)(body));
  const nativeId = getNativeId(clickTarget);
  const elementId = getGuidNullable(nativeId);

  const element =
    root && elementId
      ? Expressions.getElementById(EElement(root), elementId)
      : null;

  // Handle click on the "Log in" button for project with user systems
  if (element && isUserLogOutLogInType(element)) {
    event.preventDefault();
    onElementClicked(element);
  }

  // If place order button clicked navigate to order confirmation
  if (element && isCommerceCheckoutPlaceOrderButton(element)) {
    onElementClicked(element);
  }

  // Handle routing for the cart checkout and buy now button to go to checkout page
  if (element && isCommerceCartCheckoutButton(element)) {
    event.preventDefault();
    onElementClicked(element);
    return;
  }

  // Handle buy now click - should check ATC selection is valid then route to checkout
  if (element && isCommerceBuyNowButton(element)) {
    event.preventDefault();

    let addToCartForm: HTMLFormElement | null = clickTarget as HTMLFormElement;
    while (addToCartForm) {
      if (
        addToCartForm.getAttribute(DATA_ATTR_NODE_TYPE) ===
        NODE_TYPE_COMMERCE_ADD_TO_CART_FORM
      ) {
        break;
      } else {
        addToCartForm = addToCartForm.parentElement as HTMLFormElement;
      }
    }

    if (!addToCartForm?.reportValidity()) {
      return;
    } else {
      onElementClicked(element);
      return;
    }
  }

  if (
    root &&
    element &&
    elementId &&
    (closestPaginationPrevious(EElement(root), elementId) ||
      closestPaginationNext(EElement(root), elementId))
  ) {
    event.preventDefault();
    const dynamoWrapper = closestDynamoWrapperElement(EElement(root), element);
    if (dynamoWrapper) {
      const clickTargetNativeIdPath = getNodeNativeIdPath(clickTarget);

      const focusedSymbolNativeIdPath = clickTargetNativeIdPath.slice(0, -1);
      const wrapperNativeIdPath = [
        ...focusedSymbolNativeIdPath,
        dynamoWrapper.id,
      ];

      const currentPageNumber = getPaginationPageNumber(
        paginationPageNumbers,
        wrapperNativeIdPath
      );
      const payload = {
        wrapperNativeIdPath,
        pageNumber: closestPaginationPrevious(EElement(root), elementId)
          ? currentPageNumber - 1
          : currentPageNumber + 1,
      };
      dispatch({type: PAGINATION_PAGE_SWITCHED, payload});
    }
    return;
  }

  const nativeLinkNode = getNativeLinkNodeFromEventTarget(clickTarget);

  // We check if we can get back the nativeNode attributes
  if (
    !(
      nativeLinkNode &&
      nativeLinkNode.nodeName &&
      'getAttribute' in nativeLinkNode
    )
  ) {
    if (!('tagName' in clickTarget)) {
      return;
    }

    const el = clickTarget as Element;

    const isInput = el.nodeName.toLowerCase() === 'input';
    const inputType = el.getAttribute('type');

    // Allow specific input types to work naturally (radio and range)
    if (isInput && (inputType === 'radio' || inputType === 'range')) {
      return;
    }

    event.preventDefault();
    return;
  }

  if (
    !(
      'nodeType' in nativeLinkNode &&
      nativeLinkNode.nodeType === Node.ELEMENT_NODE &&
      'tagName' in nativeLinkNode
    )
  ) {
    return;
  }

  const nativeHtmlElement = nativeLinkNode as HTMLElement;

  // Get URL
  const href =
    getAttributeHref(nativeHtmlElement) || getAttributeHref(clickTarget);

  // Catch missing hrefs
  if (href == null) {
    return;
  }

  // Allow javascript:void(0);
  // eslint-disable-next-line no-script-url
  if (href === 'javascript:void(0);') {
    event.preventDefault();
    return;
  }

  // Links with `javascript:` URIs are blocked in designer, we show an error in console instead
  // eslint-disable-next-line no-script-url
  if (href.startsWith('javascript:')) {
    return;
  }

  // Suppress scroll to top
  if (href.endsWith('#')) {
    event.preventDefault();
    return;
  }

  // For in-page anchors, return early and defer to webflow-scroll.js
  if (href.startsWith('#')) {
    return;
  }

  // Allow legacy in-page navigation
  if (href.startsWith('#page-nav')) {
    return;
  }

  // Handle internal links to static and detail pages
  if (href.startsWith('/') && (!hrefPrefix || href.startsWith(hrefPrefix))) {
    event.preventDefault();

    await onInternalLinkClicked(href);

    return;
  }

  if (href.startsWith('tel:')) {
    // Suppress clicks on mobile-only telephone links
    event.preventDefault();
    window.alert("Oops! Click-to-call links don't work on this device.");
    return;
  }

  // Get target attributes
  const target =
    getAttributeTarget(nativeHtmlElement) || getAttributeTarget(clickTarget);
  const openNewTab = target === '_blank';

  // Allow external links to open new tab
  if (openNewTab) {
    return;
  }

  event.preventDefault();

  // Force all other external links to open in a new tab, so that the iframe doesn't navigate away
  window.open(href, '_blank');

  return;
}

const isSvgElement = (el: unknown): el is SVGElement =>
  el instanceof SVGElement;

const isHTMLElement = (el: unknown): el is HTMLElement => {
  if (!el) {
    return false;
  }

  return (
    typeof el === 'object' &&
    'nodeType' in el &&
    el.nodeType === Node.ELEMENT_NODE
  );
};

export function getClickTarget(
  event: React.MouseEvent<HTMLElement, MouseEvent>
) {
  let clickTarget = event.target as HTMLElement | SVGElement;

  if (clickTarget == null) {
    return clickTarget;
  }

  // Prevent lottie from opening up a URL link by ensuring that the target is
  // the lottie element (which maps to an element), no matter how nested
  // the SVG element is in the lottie
  if (
    clickTarget.nodeName === 'svg' ||
    (clickTarget as SVGElement).ownerSVGElement != null
  ) {
    // @ts-expect-error - Type 'ParentNode' is not assignable to type 'HTMLElement | SVGElement'.
    clickTarget = maybeGetNativeLottieElementFromTarget(clickTarget);
  }

  // Handle code components: the actual element is inside the code island shadow DOM
  if (clickTarget?.nodeName.toLowerCase() === 'code-island') {
    const paths = event.nativeEvent.composedPath?.();
    const firstElement = paths.find(
      (n): n is HTMLElement | SVGElement => isHTMLElement(n) || isSvgElement(n)
    );

    if (firstElement) {
      // Only if the click target is a link or submit button should we return it, otherwise we want to return the code island element
      const nativeLinkNode = getNativeLinkNodeFromEventTarget(clickTarget);
      const attributeType = getAttributeType(firstElement);
      const href = isHTMLElement(nativeLinkNode)
        ? getAttributeHref(nativeLinkNode)
        : getAttributeHref(firstElement);

      if (href || attributeType === 'submit') {
        clickTarget = firstElement;
      }
    }
  }

  return clickTarget;
}

/**
 * This function is called when an internal link (to static or dynamic page) is clicked while in preview mode.
 */
export async function onInternalLinkClickedInPreview({
  hrefPrefix,
  href,
  collectionState,
  shortName,
  currentLocale,
}: {
  hrefPrefix: string | null | undefined;
  href: string;
  collectionState: CollectionStateType;
  shortName: string;
  currentLocale: Locale | undefined;
}) {
  const localeSubdir = LocaleUtils.getSubdirectory(currentLocale);
  const localeAwareSlugPrefix = localeSubdir ? '/' + localeSubdir : '';
  const hrefWithoutPrefix = hrefPrefix ? href.slice(hrefPrefix.length) : href;
  const strippedHref = hrefWithoutPrefix.slice(localeAwareSlugPrefix.length);
  const hrefFragments = strippedHref.split('/');
  // We want to pick pageSlug from second to the last fragment
  // because the first fragment can be siteBasePath (site-wide page link href prefix).
  const pageSlug = _.last(_.initial(hrefFragments));

  const targetCollectionId =
    pageSlug &&
    collectionState.collections.find((c) => c.slug === pageSlug)?._id;

  // Static page
  if (!targetCollectionId) {
    navigateToStaticPageBySlug(hrefWithoutPrefix, currentLocale);
    return;
  }

  // Detail page
  const itemSlug = _.last(hrefFragments);
  const collectionItems = selectCmsItemsForLocales(
    collectionState,
    currentLocale ? [currentLocale] : null
  );

  const dynItem =
    collectionItems.find(
      (item) =>
        item.get('_cid') === targetCollectionId && item.get('slug') === itemSlug
    ) ||
    (await getCollectionItemBySlug({
      collection: collectionState.collections.get(targetCollectionId),
      //@ts-expect-error - Type 'string | undefined' is not assignable to type 'string'.
      slug: itemSlug,
      siteShortName: shortName,
    }));

  if (dynItem) {
    const dynItemId = dynItem.get('_id');
    navigateToDetailPageBySlug(pageSlug, dynItemId);
  }
}

/**
 * This method is called when a form is submitted while in preview mode.
 * @param  {event}             mouseEvent          The mouse click event
 * @return {*}
 */
function onPreviewFormSubmit(
  event: React.MouseEvent<HTMLElement, MouseEvent>,
  clickTarget: HTMLElement | SVGElement
) {
  // Stop before this bubbles to other submit handlers
  event.stopPropagation();

  // Was already prevented, nothing to do here
  if (event.defaultPrevented) {
    return;
  }

  const eventTarget = clickTarget as HTMLFormElement;
  const form = eventTarget.form;
  if (!form) {
    return;
  }

  if (!form.checkValidity()) {
    return form.reportValidity();
  }

  // If not prevented, this is probably a regular GET or POST, we should let the user know
  if (
    eventTarget &&
    eventTarget.parentElement &&
    eventTarget.parentElement.getAttribute('action') === '/search'
  ) {
    // Special-case search form #13165
    window.alert(
      'Try search on your published site, or in the search result elementʼs settings — it doesnʼt work in preview mode.'
    );
  } else if (
    eventTarget &&
    eventTarget.parentElement &&
    eventTarget.parentElement.getAttribute(DATA_ATTR_NODE_TYPE) ===
      NODE_TYPE_COMMERCE_ADD_TO_CART_FORM
  ) {
    const iframeDocument = eventTarget.ownerDocument;

    // get how many carts are in the page
    const cartsInPage = iframeDocument.querySelectorAll(
      `[data-node-type="${NODE_TYPE_COMMERCE_CART_WRAPPER}"]`
    );

    // get how many carts are in the page, that should open when adding items to the cart
    const cartsInPageToBeShown = iframeDocument.querySelectorAll(
      `[${DATA_ATTR_OPEN_PRODUCT}]`
    );

    // check if no cart is in the page and warn them that they have no cart in the page
    if (cartsInPage.length === 0) {
      window.alert(
        'Add a cart to this page and it will open by default when clicking "Add To Cart".'
      );
    } else {
      // run if a cart is in the page
      if (cartsInPageToBeShown.length > 0) {
        // run if a cart is in the page and it should open
        // if cart is in page, and should open (based on '[data-open-product]'), then open it
        cartsInPageToBeShown.forEach((cart) => {
          // create custom event and dispatch on cart elements in the page so cart will open
          iframeDocument.defaultView &&
            cart.dispatchEvent(
              new iframeDocument.defaultView.CustomEvent(CHANGE_CART_EVENT, {
                bubbles: true,
                detail: {open: true},
              })
            );
        });
      }
    }
  } else {
    window.alert(
      'Try the form on your published site — it doesn’t work in preview mode.'
    );
  }
  event.preventDefault();
  return false;
}

export const onElementClickedInPreview = ({
  element,
  currentLocale,
}: {
  element: ExpressionElement;
  currentLocale: Locale | undefined;
}) => {
  const localeSubdir = LocaleUtils.getSubdirectory(currentLocale);
  const localeAwareSlugPrefix = localeSubdir ? '/' + localeSubdir : '';

  // Handle click on the "Log in" button for project with user systems
  if (element && isUserLogOutLogInType(element)) {
    navigateToStaticPageBySlug(
      `${localeAwareSlugPrefix}/log-in`,
      currentLocale
    );
    return;
  }

  // If place order button clicked navigate to order confirmation
  if (element && isCommerceCheckoutPlaceOrderButton(element)) {
    navigateToStaticPageBySlug(
      `${localeAwareSlugPrefix}/order-confirmation`,
      currentLocale
    );
    return;
  }

  // Handle routing for the cart checkout and buy now button to go to checkout page
  if (element && isCommerceCartCheckoutButton(element)) {
    navigateToStaticPageBySlug(
      `${localeAwareSlugPrefix}/checkout`,
      currentLocale
    );
    return;
  }

  // Handle buy now click - should check ATC selection is valid then route to checkout
  if (element && isCommerceBuyNowButton(element)) {
    navigateToStaticPageBySlug(
      `${localeAwareSlugPrefix}/checkout`,
      currentLocale
    );
    return;
  }
};
